import React, { useEffect, useLayoutEffect, useState } from "react";
import { useAppContext } from "../../../context/AppContext";
import { CoursesTrans } from "../../../utils/translations";
import { getCourses } from "../../../services/courseService";

const courseCardShellClass =
  "group flex aspect-square w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl shadow-gray-900/10 transition-shadow duration-300 hover:shadow-2xl hover:shadow-gray-900/15";

function CourseCardImageWell({ children, centerContent }) {
  return (
    <div
      className={`relative h-1/2 w-full shrink-0 overflow-hidden ${centerContent ? "flex items-center justify-center" : ""}`}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(148deg,#fffefb_0%,#f7efe4_22%,#ecd9b8_52%,#d4b78a_82%,#b8956a_100%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_95%_75%_at_12%_6%,rgba(255,255,255,0.55),transparent_48%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_88%_92%,rgba(114,82,59,0.16),transparent_52%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(255,255,255,0.08),transparent_42%,rgba(255,255,255,0.12))]"
      />
      {children}
    </div>
  );
}

export default function Courses() {
  const { language, token } = useAppContext();
  const t = CoursesTrans[language] || CoursesTrans.english;
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCourses = async () => {
      setIsLoading(true);
      setError("");

      try {
        const data = await getCourses(token);
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => (a.order || 0) - (b.order || 0))
          : [];

        if (isMounted) {
          setCourses(sorted);
        }
      } catch (err) {
        if (isMounted) {
          console.error(err);
          setError(t.error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCourses();
    return () => {
      isMounted = false;
    };
  }, [t.error, token]);

  return (
    <main className="flex min-h-screen flex-col">
      <div className="h-[55vh] w-full shrink-0 overflow-hidden">
        <img
          src="/wallpaper/mat.png"
          alt=""
          className="h-full w-full object-cover object-[center_25%]"
        />
      </div>
      <div className="w-full flex-1 px-5 py-8 pb-16 lg:px-16">
        <div className="mx-auto w-full max-w-7xl">
          <header className="text-center">
            <h1 className="font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
              {t.title}
            </h1>
            <div
              className="mx-auto mt-5 h-px w-16 bg-gradient-to-r from-transparent via-brown/40 to-transparent"
              aria-hidden
            />
          </header>

          {isLoading && (
            <div className="mt-14 flex justify-center">
              <p className="text-sm tracking-wide text-ink-light animate-pulse">{t.loading}</p>
            </div>
          )}

          {!isLoading && error && (
            <p className="mt-14 text-center text-sm text-red-700">{error}</p>
          )}

          {!isLoading && !error && courses.length > 0 && (
            <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
              {courses.map((course) => (
                <article key={course.slug} className={courseCardShellClass}>
                  <CourseCardImageWell centerContent={course.slug === "sanskrit-asanas"}>
                    {course.thumbnail ? (
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        className={
                          course.slug === "sanskrit-asanas"
                            ? "relative z-[1] h-full w-full max-h-full object-contain object-center p-3 sm:p-4"
                            : "relative z-[1] h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        }
                        decoding="async"
                      />
                    ) : (
                      <div
                        aria-hidden
                        className="absolute inset-0 z-[1] bg-gradient-to-t from-brown/[0.07] to-transparent"
                      />
                    )}
                  </CourseCardImageWell>
                  <div className="flex min-h-0 flex-1 flex-col items-center overflow-hidden p-4 sm:p-5 text-center">
                    <h2 className="w-full shrink-0 text-center font-serif text-lg font-medium leading-snug text-ink line-clamp-2">
                      {course.title}
                    </h2>
                    {course.description && (
                      <p className="mt-1.5 min-h-0 w-full flex-1 text-sm leading-relaxed text-ink-mid line-clamp-3">
                        {course.description}
                      </p>
                    )}
                    <div className="mt-auto flex w-full shrink-0 flex-col items-center pt-2">
                      {(course.locked || course.nisPrice > 0 || course.classCount > 0) && (
                        <p className="text-xs font-medium uppercase tracking-wider text-ink-light">
                          {course.locked
                            ? course.nisPrice > 0
                              ? `${t.locked} · ${course.nisPrice} ₪`
                              : t.locked
                            : course.nisPrice > 0
                              ? t.unlocked
                              : `${course.classCount} ${t.classes}`}
                        </p>
                      )}
                      <a
                        href={course.slug === "sanskrit-asanas" ? "/yoga-asanas" : `/courses/${course.slug}`}
                        className="mt-2 inline-flex rounded-full bg-brown px-4 py-2 text-sm font-medium text-white transition hover:bg-clay-dark"
                      >
                        {course.locked && course.nisPrice > 0 ? t.buy : t.open}
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
